import { ReactNode } from 'react';
// 组件
import SimpleForm, { SimpleFormItemProps } from '../../SimpleForm';
// 方法
import { getInitialValue, getValueName } from '../utils';
// 常量
import { EditVirtualTableColumnType } from '../interface';

function getFormItem(
  record: any,
  props?: EditVirtualTableColumnType,
  rowIndex?: number,
): ReactNode {
  if (!props) return null;
  const {
    formItemType,
    name,
    initialValue,
    required,
    rules,
    form,
    disabledRender,
    disabledRenderKey,
    enumCode,
    style,
    disabled,
    relativePathName,
    afterFormItemProps,
    inputProps,
    inputNumberProps,
    switchProps,
    checkboxProps,
    checkboxGroupProps,
    datePickerProps,
    rangePickerProps,
    timePickerProps,
    selectProps,
    superSelectProps,
    selectTableProps,
    superSelectTableProps,
    uploadProps,
    uploadImageProps,
    uploadImagesProps,
    superAutoCompleteProps,
    formatRules,
  } = props;
  const relativePath = relativePathName
    ? record[relativePathName]
    : initialValue;
  const baseSearchFormProps: SimpleFormItemProps = {
    name,
    form,
    initialValue: getInitialValue(initialValue, record, name),
    rules: formatRules ? formatRules(record) : rules,
  };
  if (disabledRenderKey && !disabledRender) {
    baseSearchFormProps.disabledRender = record[disabledRenderKey];
  }
  if (required && !baseSearchFormProps.rules) {
    baseSearchFormProps.rules = [
      {
        required,
      },
    ];
  }
  const baseFormProps = { disabled };
  switch (formItemType) {
    case 'Input':
      return (
        <SimpleForm.Input
          {...baseSearchFormProps}
          inputProps={{ ...baseFormProps, ...inputProps }}
        />
      );
    case 'Input':
      return (
        <SimpleForm.SuperAutoComplete
          {...baseSearchFormProps}
          superAutoCompleteProps={{ ...baseFormProps, ...superAutoCompleteProps }}
        />
      );
    case 'InputNumber':
      return (
        <SimpleForm.InputNumber
          {...baseSearchFormProps}
          inputNumberProps={{ ...baseFormProps, ...inputNumberProps }}
        />
      );
    case 'Switch':
      if (baseSearchFormProps.initialValue === undefined) {
        baseSearchFormProps.initialValue = false;
      }
      return (
        <SimpleForm.Switch
          {...baseSearchFormProps}
          switchProps={{ ...baseFormProps, ...switchProps }}
        />
      );
    case 'Checkbox':
      return (
        <SimpleForm.Checkbox
          {...baseSearchFormProps}
          checkboxProps={{ ...baseFormProps, ...checkboxProps }}
        />
      );
    case 'CheckboxGroup':
      return (
        <SimpleForm.CheckboxGroup
          {...baseSearchFormProps}
          enumCode={enumCode}
          checkboxGroupProps={{ ...baseFormProps, ...checkboxGroupProps }}
        />
      );
    case 'DatePicker':
      return (
        <SimpleForm.DatePicker
          {...baseSearchFormProps}
          datePickerProps={{ ...baseFormProps, ...datePickerProps }}
        />
      );
    case 'RangePicker':
      return (
        <SimpleForm.RangePicker
          {...baseSearchFormProps}
          rangePickerProps={{ ...baseFormProps, ...rangePickerProps }}
        />
      );
    case 'TimePicker':
      return (
        <SimpleForm.TimePicker
          {...baseSearchFormProps}
          timePickerProps={{ ...baseFormProps, ...timePickerProps }}
        />
      );
    case 'Select':
      return (
        <SimpleForm.Select
          {...baseSearchFormProps}
          enumCode={enumCode}
          selectProps={{ ...baseFormProps, ...selectProps }}
        />
      );
    case 'SuperSelect':
      return (
        <SimpleForm.SuperSelect
          {...baseSearchFormProps}
          enumCode={enumCode}
          superSelectProps={{
            isDisabledEllipsis: true,
            disabledBorderDashed: false,
            ...baseFormProps,
            ...superSelectProps,
          }}
        />
      );
    case 'SelectTable':
      if (!selectTableProps) return null;
      let initOptions: any[] = [];
      let namekey = props?.namekey;
      let valuekey = props?.valuekey;
      let label = selectTableProps?.nameKey;
      let value = selectTableProps?.valueKey || getValueName(name);
      if (namekey && valuekey && value && label) {
        const labels = record[namekey];
        const values = record[valuekey];
        if (labels !== undefined && values !== undefined) {
          if (Array.isArray(values)) {
            values.forEach((v, k) => {
              initOptions.push({
                [label as string]: labels[k],
                [value as string]: v,
              });
            });
          } else {
            initOptions.push({
              [label]: labels,
              [value]: values,
            });
          }
        }
      }
      return (
        <SimpleForm.SelectTable
          {...baseSearchFormProps}
          selectTableProps={{
            initOptions: initOptions.length ? initOptions : undefined,
            ...baseFormProps,
            ...selectTableProps,
            beforeFetch: (params: any, record?: any) => selectTableProps.beforeFetch?.(params, record, rowIndex),
          }}
        />
      );
    case 'SuperSelectTable':
      if (!superSelectTableProps) return null;
      let initOptions_1: any[] = [];
      let labelKey_1 = props?.labelKey;
      let valueKey_1 = props?.valueKey || getValueName(name);
      let label_1 = superSelectTableProps?.labelKey;
      let value_1 = superSelectTableProps?.valueKey;
      if (labelKey_1 && valueKey_1 && label_1 && value_1) {
        const labels = record[labelKey_1];
        const values = record[valueKey_1];
        if (labels !== undefined && values !== undefined) {
          if (Array.isArray(values)) {
            values.forEach((v, k) => {
              initOptions_1.push({
                [label_1 as string]: labels[k],
                [value_1 as string]: v,
              });
            });
          } else {
            initOptions_1.push({
              [label_1]: labels,
              [value_1]: values,
            });
          }
        }
      }
      return (
        <SimpleForm.SuperSelectTable
          {...baseSearchFormProps}
          superSelectTableProps={{
            initOptions: initOptions_1.length ? initOptions_1 : undefined,
            ...baseFormProps,
            ...superSelectTableProps,
            beforeFetch: (params: any, record?: any) => superSelectTableProps.beforeFetch?.(params, {...record, ...(form?.getFieldsValue() ?? {})}, rowIndex),
            isOpenUpdate: superSelectTableProps.isOpenUpdate || !!superSelectTableProps.beforeFetch,
            onChange: (value: any, item: any) => superSelectTableProps.onChange?.(value, item, form, rowIndex, {...record, ...(form?.getFieldsValue() ?? {})},),
          }}
        />
      );
    case 'Upload':
      return (
        <SimpleForm.Upload
          {...baseSearchFormProps}
          initialValue={initialValue}
          uploadProps={{ ...baseFormProps, ...uploadProps }}
        />
      );
    case 'UploadImage':
      return (
        <SimpleForm.UploadImage
          {...baseSearchFormProps}
          initialValue={initialValue}
          style={style}
          uploadImageProps={{ ...baseFormProps, ...uploadImageProps }}
        />
      );
    case 'UploadImages':
      return (
        <SimpleForm.UploadImages
          {...baseSearchFormProps}
          initialValue={initialValue}
          uploadImagesProps={{ ...baseFormProps, ...uploadImagesProps }}
        />
      );
    default:
      return (
        <SimpleForm.Input
          {...baseSearchFormProps}
          inputProps={{ ...baseFormProps, ...inputProps }}
        />
      );
  }
}

export default getFormItem;
